import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const clearSession = () => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('theme');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState('claro');

  useEffect(() => {
    const initializeAuth = async () => {
      const token = sessionStorage.getItem('token');
      const savedUser = sessionStorage.getItem('user');
      const savedTheme = sessionStorage.getItem('theme') || 'claro';

      if (token && savedUser) {
        if (isTokenExpired(token)) {
          clearSession();
          window.location.href = '/agenda/login';
          return;
        }
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          setTheme(savedTheme);
        } catch (error) {
          clearSession();
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      
      // ✅ Usar sessionStorage (persiste aunque recargues)
      sessionStorage.setItem('token', data.token);
      sessionStorage.setItem('user', JSON.stringify(data.user));
      
      setUser(data.user);
      
      const userTheme = data.user.tenant?.tema || 'claro';
      setTheme(userTheme);
      sessionStorage.setItem('theme', userTheme);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Error al iniciar sesión' 
      };
    }
  };

  const register = async (formData) => {
    try {
      const { data } = await api.post('/auth/register', formData);
      return { success: true, requiere_verificacion: !!data.requiere_verificacion };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Error al registrar'
      };
    }
  };

  const logout = () => {
    clearSession();
    setUser(null);
    window.location.href = '/agenda/login';
  };

  const toggleTheme = () => {
    const newTheme = theme === 'claro' ? 'oscuro' : 'claro';
    setTheme(newTheme);
    sessionStorage.setItem('theme', newTheme);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/auth/me');
      sessionStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } catch {
      return null;
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    theme,
    toggleTheme,
    refreshUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};